<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useCategoryStore } from '../stores/categoryStore';
import { useProductStore } from '../stores/productStore';
import { useNotificationStore } from '../stores/notificationStore';
import Modal from './Modal.vue';

const categoryStore = useCategoryStore();
const productStore = useProductStore();
const notificationStore = useNotificationStore();

const showModal = ref(false);
const editingCategory = ref<string | null>(null);
const newCategoryName = ref('');
const processing = ref(false);

onMounted(async () => {
  await categoryStore.fetchCategories();
});

const handleEdit = (categoryId: string, currentName: string) => {
  editingCategory.value = categoryId;
  newCategoryName.value = currentName;
  showModal.value = true;
};

const handleDelete = async (categoryId: string, categoryName: string) => {
  if (!confirm(`¿Está seguro de que desea eliminar la categoría "${categoryName}"? Esta acción no se puede deshacer.`)) {
    return;
  }

  processing.value = true;
  try {
    await categoryStore.deleteCategory(categoryId);
    notificationStore.success('Categoría eliminada correctamente');
  } catch (error) {
    notificationStore.error(error instanceof Error ? error.message : 'Error al eliminar la categoría');
  } finally {
    processing.value = false;
  }
};

const handleSubmit = async () => {
  if (!newCategoryName.value.trim()) {
    notificationStore.error('El nombre de la categoría no puede estar vacío');
    return;
  }

  processing.value = true;
  try {
    if (editingCategory.value) {
      await categoryStore.updateCategoryName(editingCategory.value, newCategoryName.value);
      notificationStore.success('Categoría actualizada correctamente');
    } else {
      await categoryStore.addCategory(newCategoryName.value);
      notificationStore.success('Categoría creada correctamente');
    }
    closeModal();
  } catch (error) {
    notificationStore.error('Error al guardar la categoría');
  } finally {
    processing.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
  editingCategory.value = null;
  newCategoryName.value = '';
};

const getCategoryProducts = (categoryId: string) => {
  return productStore.products.filter(product => product.Categoria === categoryId);
};
</script>

<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold text-slate-800">Gestión de Categorías</h2>
      <button @click="showModal = true" class="btn-primary">
        Nueva Categoría
      </button>
    </div>

    <div v-if="categoryStore.loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="[id, label] in Object.entries(categoryStore.categories)"
        :key="id"
        class="bg-white rounded-lg shadow-sm border border-slate-200 p-6"
      >
        <div class="flex justify-between items-start">
          <div>
            <h3 class="text-lg font-semibold text-slate-800">{{ label }}</h3>
            <p class="text-sm text-slate-600 mt-2">
              {{ getCategoryProducts(id).length }} productos
            </p>
          </div>
          <div class="flex flex-col space-y-2">
            <button
              @click="handleEdit(id, label)"
              class="text-indigo-600 hover:text-indigo-800"
              :disabled="processing"
            >
              Editar
            </button>
            <button
              @click="handleDelete(id, label)"
              class="text-red-600 hover:text-red-800"
              :disabled="processing"
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>

    <Modal
      :show="showModal"
      :title="editingCategory ? 'Editar Categoría' : 'Nueva Categoría'"
      @close="closeModal"
    >
      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Nombre de la Categoría
          </label>
          <input
            v-model="newCategoryName"
            class="input-field"
            required
            placeholder="Ingrese el nombre de la categoría"
            :disabled="processing"
          />
        </div>
        
        <div class="flex justify-end space-x-4">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 text-slate-600 hover:text-slate-800"
            :disabled="processing"
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            class="btn-primary"
            :disabled="processing"
          >
            {{ processing ? 'Guardando...' : (editingCategory ? 'Guardar Cambios' : 'Crear Categoría') }}
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>